import * as flags from "country-flag-icons/react/3x2";
import { getCountries, CountryCode } from "libphonenumber-js";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ElementType, FocusEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import BasePhoneInput, { getCountryCallingCode, isPossiblePhoneNumber } from "react-phone-number-input";
import type { Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import Label from "../Label";
import { countryLabels } from "./countryLabels";
import {
  CountryButton,
  CountryDivider,
  CountryDropdown,
  CountryOptionButton,
  CountryOptions,
  CountrySearch,
  CountrySelectWrapper,
  PhoneField,
  StyledPhoneInputWrapper,
} from "./styles";

// Países de maior procura pelos nossos usuários (a lista completa continua disponível
// abaixo, em ordem alfabética) — pedido explícito do produto, não uma heurística.
const PRIORITY_COUNTRIES: CountryCode[] = ["BR", "PT", "US", "IT", "FR", "ES", "DE", "AR", "RU", "CA", "CL", "PE"];

type Props = {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
};

export const normalizePhoneValue = (value?: string) => {
  const trimmed = value?.trim() || "";

  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("+")) {
    return trimmed;
  }

  const digits = trimmed.replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

export const sanitizePhoneDigits = (value?: string) => normalizePhoneValue(value).replace(/\D/g, "");

export const isPossibleWhatsappNumber = (value?: string) => {
  const normalizedValue = normalizePhoneValue(value);
  return !!normalizedValue && isPossiblePhoneNumber(normalizedValue);
};

type CountryOption = {
  value?: string;
  label: string;
};

type CountrySelectProps = {
  value?: string;
  onChange: (value?: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  options: CountryOption[];
  iconComponent: ElementType;
  disabled?: boolean;
};

const normalizeSearchText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Mapa país -> código de discagem, calculado uma única vez (evita recalcular
// getCountryCallingCode a cada opção renderizada e a cada tecla digitada na busca).
const callingCodeByCountry = getCountries().reduce<Record<string, string>>((acc, country) => {
  acc[country] = `+${getCountryCallingCode(country)}`;
  return acc;
}, {});

const getCallingCode = (country?: string) => (country ? callingCodeByCountry[country as CountryCode] || "" : "");

const SearchableCountrySelect = ({
  value,
  onChange,
  onFocus,
  onBlur,
  options,
  iconComponent: Icon,
  disabled,
}: CountrySelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  // Sem fallback para "primeira opção com valor" aqui de propósito: quando a lib não
  // consegue determinar o país a partir dos dígitos (ex.: DDD norte-americano
  // inexistente, como 555), `value` chega undefined. Cair num país arbitrário (hoje
  // seria o primeiro da lista) mostra uma bandeira errada e engana o usuário; melhor
  // não mostrar bandeira nenhuma, como já é o padrão da própria opção "internacional".
  const selectedOption = options.find(option => option.value === value);

  // Traz PRIORITY_COUNTRIES para o topo da lista, na ordem definida ali, mantendo o
  // restante em ordem alfabética (a ordem que a própria lib já entrega em `options`).
  const { priorityOptions, restOptions } = useMemo(() => {
    const byValue = new Map(options.map(option => [option.value, option]));
    const priority = PRIORITY_COUNTRIES.map(code => byValue.get(code)).filter((option): option is CountryOption =>
      Boolean(option)
    );
    const prioritySet = new Set<string>(PRIORITY_COUNTRIES);
    const rest = options.filter(option => !option.value || !prioritySet.has(option.value));
    return { priorityOptions: priority, restOptions: rest };
  }, [options]);

  const sortedOptions = useMemo(() => [...priorityOptions, ...restOptions], [priorityOptions, restOptions]);

  const isSearching = search.trim() !== "";

  const filteredOptions = useMemo(() => {
    const normalizedSearch = normalizeSearchText(search.trim());

    if (!normalizedSearch) {
      return sortedOptions;
    }

    return sortedOptions.filter(option => {
      const callingCode = getCallingCode(option.value);
      const searchable = normalizeSearchText(`${option.label} ${option.value || ""} ${callingCode}`);
      return searchable.includes(normalizedSearch);
    });
  }, [sortedOptions, search]);

  const openDropdown = () => {
    if (disabled) {
      return;
    }

    setOpen(true);
    onFocus?.();
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };

  const closeDropdown = () => {
    setOpen(false);
    setSearch("");
    onBlur?.();
  };

  // A scoped effect (not `useKeyPress`) keeps the listener alive only while the
  // dropdown is actually open.
  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    window.addEventListener("keyup", onKeyUp);
    return () => window.removeEventListener("keyup", onKeyUp);
  }, [open]);

  const selectCountry = (country?: string) => {
    onChange(country);
    closeDropdown();
  };

  const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocusTarget = event.relatedTarget as Node | null;

    if (!event.currentTarget.contains(nextFocusTarget)) {
      closeDropdown();
    }
  };

  return (
    <CountrySelectWrapper onBlur={handleWrapperBlur}>
      <CountryButton
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selectedOption ? `${selectedOption.label} ${getCallingCode(selectedOption.value)}` : undefined}
        onClick={() => (open ? closeDropdown() : openDropdown())}
        disabled={disabled}>
        {selectedOption?.value && <Icon country={selectedOption.value} label={selectedOption.label} />}
        <span className="arrow" />
      </CountryButton>
      {open && (
        <CountryDropdown>
          <CountrySearch
            ref={searchRef}
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t("components.phoneNumberInput.searchCountry")}
          />
          <CountryOptions role="listbox">
            {filteredOptions.map((option, index) => (
              <Fragment key={option.value || "international"}>
                {!isSearching && index === priorityOptions.length && priorityOptions.length > 0 && <CountryDivider />}
                <CountryOptionButton
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  $active={option.value === value}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => selectCountry(option.value)}>
                  {option.value ? <Icon country={option.value} label={option.label} /> : <span />}
                  <span className="country-name">{option.label}</span>
                  <span className="calling-code">{getCallingCode(option.value)}</span>
                </CountryOptionButton>
              </Fragment>
            ))}
          </CountryOptions>
        </CountryDropdown>
      )}
    </CountrySelectWrapper>
  );
};

const PhoneNumberInput = ({ id, label, value, onChange, required, icon, disabled }: Props) => {
  const { i18n } = useTranslation();
  const [focus, setFocus] = useState(false);
  const normalizedValue = normalizePhoneValue(value);
  const labels = useMemo(() => countryLabels(i18n.language), [i18n.language]);

  return (
    <>
      <PhoneField focus={focus}>
        {icon}
        <StyledPhoneInputWrapper>
          <BasePhoneInput
            id={id}
            name={id}
            value={normalizedValue as Value}
            labels={labels}
            flags={flags}
            defaultCountry="BR"
            international
            countryCallingCodeEditable={false}
            countrySelectComponent={SearchableCountrySelect}
            smartCaret={false}
            disabled={disabled}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onChange={phone => onChange(phone || "")}
          />
        </StyledPhoneInputWrapper>
      </PhoneField>
      <Label htmlFor={id} required={required} text={label} />
    </>
  );
};

export default PhoneNumberInput;
